import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Scan, ListChecks, Keyboard } from "@phosphor-icons/react";
import SantriSelect from "@/components/SantriSelect";
import RowActions from "@/components/RowActions";
import QRScanner from "@/components/QRScanner";

const SESI = ["pagi", "siang", "sore"];
const STATUS = ["makan", "tidak_makan", "puasa"];

export default function Makan() {
  const [tab, setTab] = useState("massal");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ santri: null, tanggal: new Date().toISOString().slice(0, 10), sesi: "pagi", status: "makan", keterangan: "" });
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [editing, setEditing] = useState(null);
  const [santriList, setSantriList] = useState([]);
  const [kelompokTipe, setKelompokTipe] = useState("kamar");
  const [kelompokId, setKelompokId] = useState("");
  const [master, setMaster] = useState({ kamar: [], kelas: [], halaqah: [] });
  const [rowStatus, setRowStatus] = useState({});
  const [bulkTanggal, setBulkTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [bulkSesi, setBulkSesi] = useState("pagi");
  const [scanOpen, setScanOpen] = useState(false);
  const [qrTanggal, setQrTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [qrSesi, setQrSesi] = useState("pagi");

  const load = async () => { const { data } = await api.get("/makan", { params: { tanggal: dateFilter } }); setItems(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFilter]);
  useEffect(() => {
    Promise.all([api.get("/master/kamar"), api.get("/master/kelas"), api.get("/master/halaqah")])
      .then(([k, ke, h]) => setMaster({ kamar: k.data, kelas: ke.data, halaqah: h.data }));
  }, []);
  useEffect(() => {
    if (tab !== "massal") return;
    (async () => {
      let params = {};
      if (kelompokTipe === "kamar" && kelompokId) params.kamar_id = kelompokId;
      else if (kelompokTipe === "kelas" && kelompokId) params.kelas_id = kelompokId;
      else if (kelompokTipe === "halaqah" && kelompokId) params.halaqah_id = kelompokId;
      else if (kelompokTipe !== "all") { setSantriList([]); return; }
      const { data } = await api.get("/santri", { params });
      setSantriList(data);
    })();
  }, [tab, kelompokTipe, kelompokId]);

  const submitIndividu = async (e) => {
    e.preventDefault();
    if (!form.santri?.nis) return toast.error("Pilih santri");
    try { await api.post("/makan", { ...form, nis: form.santri.nis }); toast.success("Tercatat"); setForm({ ...form, santri: null, keterangan: "" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const submitBulk = async () => {
    const targets = santriList.filter((s) => rowStatus[s.nis]);
    if (targets.length === 0) return toast.error("Belum ada status dipilih");
    let ok = 0, fail = 0;
    for (const s of targets) {
      try { await api.post("/makan", { nis: s.nis, tanggal: bulkTanggal, sesi: bulkSesi, status: rowStatus[s.nis] }); ok++; }
      catch (_) { fail++; }
    }
    toast.success(`Berhasil: ${ok}${fail ? ` · Gagal: ${fail}` : ""}`); setRowStatus({}); load();
  };
  const onScanned = async (text) => {
    setScanOpen(false);
    try { await api.post("/makan", { nis: text, tanggal: qrTanggal, sesi: qrSesi, status: "makan" }); toast.success(`Santri NIS ${text} tercatat`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || `NIS tidak dikenali: ${text}`); }
  };
  const saveEdit = async () => {
    try { await api.put(`/makan/${editing.id}`, { status: editing.status, sesi: editing.sesi, tanggal: editing.tanggal, keterangan: editing.keterangan }); toast.success("Diperbarui"); setEditing(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Divisi Logistik</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Absensi Makan Santri</h1>
        <p className="text-sm text-slate-500 mt-1">Tiga metode: <b>Massal</b> per kamar/kelas/halaqah, <b>QR/Barcode</b>, atau <b>Individu</b> per santri.</p>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        <button onClick={() => setTab("massal")} data-testid="tab-makan-massal" className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "massal" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}><ListChecks size={14} className="inline -mt-0.5 mr-1"/> Absensi Massal</button>
        <button onClick={() => setTab("qr")} data-testid="tab-makan-qr" className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "qr" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}><Scan size={14} className="inline -mt-0.5 mr-1"/> QR / Barcode</button>
        <button onClick={() => setTab("individu")} data-testid="tab-makan-individu" className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "individu" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}><Keyboard size={14} className="inline -mt-0.5 mr-1"/> Individu</button>
      </div>

      {tab === "massal" && (
        <div className="imsyaf-card p-5 space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div><label className="text-xs">Pilih Kelompok</label>
              <select className="imsyaf-input" value={kelompokTipe} onChange={(e) => { setKelompokTipe(e.target.value); setKelompokId(""); }} data-testid="makan-kelompok-tipe">
                <option value="kamar">Per Kamar</option><option value="kelas">Per Kelas</option>
                <option value="halaqah">Per Halaqah</option><option value="all">Semua Santri</option>
              </select></div>
            {kelompokTipe !== "all" && (
              <div><label className="text-xs">{kelompokTipe}</label>
                <select className="imsyaf-input" value={kelompokId} onChange={(e) => setKelompokId(e.target.value)} data-testid="makan-kelompok-id">
                  <option value="">— Pilih —</option>
                  {master[kelompokTipe].map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select></div>
            )}
            <div><label className="text-xs">Sesi</label>
              <select className="imsyaf-input" value={bulkSesi} onChange={(e) => setBulkSesi(e.target.value)}>
                {SESI.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={bulkTanggal} onChange={(e) => setBulkTanggal(e.target.value)} /></div>
          </div>
          {santriList.length === 0 ? (
            <div className="text-center py-8 text-slate-400">Pilih kelompok di atas untuk memuat daftar santri.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm zebra">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left px-3 py-2">Santri</th><th className="text-left px-3 py-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {santriList.map((s) => (
                      <tr key={s.nis} className="border-t border-slate-100" data-testid={`bulk-makan-row-${s.nis}`}>
                        <td className="px-3 py-2 font-medium">{s.nama}<div className="text-[10px] font-mono text-slate-500">{s.kamar_nama || "-"}</div></td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            {STATUS.map((st) => (
                              <button key={st} onClick={() => setRowStatus({ ...rowStatus, [s.nis]: st })} data-testid={`makan-${s.nis}-${st}`}
                                className={`px-2 py-1 text-[11px] rounded ${rowStatus[s.nis] === st ? "bg-[#14532d] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{st.toUpperCase()}</button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={submitBulk} className="imsyaf-btn-primary" data-testid="makan-bulk-save"><Plus size={16}/> Simpan Semua ({Object.keys(rowStatus).length} santri)</button>
            </>
          )}
        </div>
      )}

      {tab === "qr" && (
        <div className="space-y-3">
          <div className="imsyaf-card p-5 grid md:grid-cols-3 gap-3">
            <div><label className="text-xs">Tanggal</label><input type="date" className="imsyaf-input" value={qrTanggal} onChange={(e) => setQrTanggal(e.target.value)} /></div>
            <div><label className="text-xs">Sesi</label><select className="imsyaf-input" value={qrSesi} onChange={(e) => setQrSesi(e.target.value)}>{SESI.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div className="flex items-end">
              <button onClick={() => setScanOpen(true)} className="imsyaf-btn-primary w-full" data-testid="makan-scan-btn"><Scan size={18}/> Buka Scanner QR/Barcode</button>
            </div>
          </div>
          {scanOpen && (
            <div className="imsyaf-card p-5">
              <QRScanner onScan={onScanned} onError={(m) => { setScanOpen(false); toast.error(m); }} />
              <div className="text-right mt-3"><button onClick={() => setScanOpen(false)} className="imsyaf-btn-outline">Tutup</button></div>
            </div>
          )}
        </div>
      )}

      {tab === "individu" && (
        <form onSubmit={submitIndividu} className="imsyaf-card p-5 grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2"><label className="text-xs">Santri (cari nama / kamar) *</label>
            <SantriSelect value={form.santri} onChange={(s) => setForm({ ...form, santri: s })} testid="makan-santri" /></div>
          <div><label className="text-xs">Tanggal</label><input type="date" className="imsyaf-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
          <div><label className="text-xs">Sesi</label><select className="imsyaf-input" value={form.sesi} onChange={(e) => setForm({ ...form, sesi: e.target.value })}>{SESI.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="text-xs">Status</label><select className="imsyaf-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div className="md:col-span-5"><button className="imsyaf-btn-primary" data-testid="makan-save-btn"><Plus size={16}/> Catat Makan</button></div>
        </form>
      )}

      <div className="imsyaf-card p-4 flex items-end gap-3">
        <div><label className="text-xs">Filter Tanggal</label><input type="date" className="imsyaf-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} /></div>
        <div className="text-sm text-slate-500 pb-2">Total: <b>{items.length}</b></div>
      </div>
      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-4 py-2">Waktu</th><th className="text-left px-4 py-2">NIS</th><th className="text-left px-4 py-2">Nama</th>
            <th className="text-left px-4 py-2">Sesi</th><th className="text-left px-4 py-2">Status</th>
            <th className="text-right px-4 py-2">Aksi</th>
          </tr></thead>
          <tbody>{items.map((m) => (
            <tr key={m.id} className="border-t border-slate-100" data-testid={`makan-row-${m.id}`}>
              <td className="px-4 py-2 text-xs">{m.created_at?.slice(11,16)}</td>
              <td className="px-4 py-2 font-mono">{m.santri_nis}</td><td className="px-4 py-2">{m.santri_nama}</td>
              <td className="px-4 py-2">{m.sesi}</td><td className="px-4 py-2">{m.status}</td>
              <td className="px-4 py-2 text-right">
                <RowActions testid={`makan-${m.id}`}
                  onEdit={() => setEditing({ ...m })}
                  onDelete={async () => { await api.delete(`/makan/${m.id}`); toast.success("Dihapus"); load(); }}
                  confirmText="Hapus data makan ini?" />
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Belum ada data.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="edit-makan-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-3">
            <h3 className="font-display text-lg font-bold text-[#064E3B]">Edit Makan — {editing.santri_nama}</h3>
            <div><label className="text-xs">Tanggal</label><input type="date" className="imsyaf-input" value={editing.tanggal} onChange={(e) => setEditing({ ...editing, tanggal: e.target.value })}/></div>
            <div><label className="text-xs">Sesi</label><select className="imsyaf-input" value={editing.sesi} onChange={(e) => setEditing({ ...editing, sesi: e.target.value })}>{SESI.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="text-xs">Status</label><select className="imsyaf-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="edit-makan-status">{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="imsyaf-btn-outline">Batal</button>
              <button onClick={saveEdit} className="imsyaf-btn-primary" data-testid="edit-makan-save">Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
